/**
 * SafeMotion - SSR-safe framer-motion wrapper
 *
 * Prevents hydration mismatches by skipping initial animations during SSR.
 */

import { createContext, useContext, ReactNode } from 'react';
import { useIsClient } from '../../hooks/useIsClient';

const AnimationContext = createContext(false);

export function SafeMotionProvider({ children }: { children: ReactNode }) {
  const isClient = useIsClient();

  return <AnimationContext.Provider value={isClient}>{children}</AnimationContext.Provider>;
}

export function useCanAnimate(): boolean {
  return useContext(AnimationContext);
}

export function useSafeAnimation<T extends Record<string, unknown>>(
  initial: T,
  animate: T,
  inView: boolean = true
): { initial: T | undefined; animate: T | undefined } {
  const canAnimate = useCanAnimate();

  if (!canAnimate) {
    return { initial: undefined, animate: undefined };
  }

  return {
    initial,
    animate: inView ? animate : initial,
  };
}

export function useSafeVariants(inView: boolean = true): { initial: string | undefined; animate: string } {
  const canAnimate = useCanAnimate();

  if (!canAnimate) {
    return { initial: undefined, animate: 'visible' };
  }

  return {
    initial: 'hidden',
    animate: inView ? 'visible' : 'hidden',
  };
}
